import React, { useState, useEffect, useRef } from 'react';
import { Contact } from '../types/Contact';
import { supabase } from '../lib/supabase';
import { createContactCore, updateContactCore } from '../lib/contactsCore';
import { uploadCompressedImage } from '../lib/imageUpload';
import { GHLSync } from '../lib/ghlSync';
import { User } from '@supabase/supabase-js';
import { ArrowLeft, Sparkles, Upload, Check, Loader2, Camera, MapPin, Phone } from 'lucide-react';
import { loadGoogleMapsPlaces, extractAddressParts, shortAddressLabel } from '../lib/googlePlaces';
import CustomSelect from './CustomSelect';
import { useGroupContext } from '../lib/groups/context';
import { addContactToGroup, findGroupBySlug } from '../lib/groups/api';

interface CreateContactPageProps {
    user?: User | null;
    onBack: () => void;
    onSave: () => void;
    editingContact?: Contact | null;
    onToggleQuickAdd?: () => void;
}

export default function CreateContactPage({ user, onBack, onSave, editingContact, onToggleQuickAdd }: CreateContactPageProps) {
    const handleBack = () => {
        // Avoid using window.history.back() which can ping-pong between view toggles
        try { onBack?.(); } catch { /* ignore */ }
    };
    // Always render the full form on this page; Quick Add lives on its own route
    const mode: 'full' = 'full';
    const [formData, setFormData] = useState<Partial<Contact>>({
        first_name: '',
        last_name: '',
        title: '',
        company: '',
        industry: '',
        email: '',
        phone: '',
        mobile: '',
        address: '',
        city: '',
        state: '',
        country: '',
        zip_code: '',
        linkedin_url: '',
        twitter_url: '',
        website_url: '',
        instagram_url: '',
        tier: 'Unassigned',
        status: 'Not Contacted',
        relationship_type: '',
        referral_source: '',
        next_follow_up: '',
        net_worth_estimate: '',
        investment_interests: '',
        business_size: '',
        revenue_estimate: '',
        birthday: '',
        spouse_name: '',
        children_names: '',
        interests: '',
        education: '',
        notes: '',
        private_notes: '',
        personal_details: '',
        is_priority: false,
        is_archived: false,
        marketing_consent: false,
        profile_image_url: '',
        additional_images: []
    });

    const [loading, setLoading] = useState(false);
    // Smart Fill state
    const [smartFillText, setSmartFillText] = useState('');
    const [smartFillLoading, setSmartFillLoading] = useState(false);
    const [smartFillError, setSmartFillError] = useState<string | null>(null);
    const [aiLoading, setAiLoading] = useState(false);
    const [ghlSyncing, setGhlSyncing] = useState(false);
    const [quickAddText, setQuickAddText] = useState('');
    const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
    const [profileImagePreview, setProfileImagePreview] = useState<string>('');

    // Quick Snap Create state
    // Normalize and map parsed fields into formData (only overwrite when value present)
    const applyParsedFields = (parsedData: any) => {
        if (!parsedData || typeof parsedData !== 'object') return;

        const cleanUrl = (u?: string | null) => {
            if (!u) return null;
            const s = String(u).trim();
            if (!s) return null;
            return s.startsWith('http') ? s : `https://${s}`;
        };
        const cleanPhone = (p?: string | null) => {
            if (!p) return null;
            const s = String(p).trim();
            if (!s) return null;
            return s.replace(/[^\d+]/g, '');
        };
        const cleanEmail = (e?: string | null) => {
            if (!e) return null;
            const s = String(e).trim();
            if (!s) return null;
            return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s) ? s : null;
        };

        setFormData(prev => ({
            ...prev,
            first_name: parsedData.first_name ?? prev.first_name,
            last_name: parsedData.last_name ?? prev.last_name,
            title: parsedData.title ?? prev.title,
            company: parsedData.company ?? prev.company,
            industry: parsedData.industry ?? prev.industry,
            email: cleanEmail(parsedData.email) ?? prev.email,
            phone: cleanPhone(parsedData.phone) ?? prev.phone,
            mobile: cleanPhone(parsedData.mobile) ?? prev.mobile,
            address: parsedData.address ?? prev.address,
            city: parsedData.city ?? prev.city,
            state: parsedData.state ?? prev.state,
            country: parsedData.country ?? prev.country,
            zip_code: parsedData.zip_code ?? prev.zip_code,
            linkedin_url: cleanUrl(parsedData.linkedin_url) ?? prev.linkedin_url,
            twitter_url: cleanUrl(parsedData.twitter_url) ?? prev.twitter_url,
            website_url: cleanUrl(parsedData.website_url) ?? prev.website_url,
            instagram_url: cleanUrl(parsedData.instagram_url) ?? prev.instagram_url,
            tier: parsedData.tier ?? prev.tier,
            status: parsedData.status ?? prev.status,
            relationship_type: parsedData.relationship_type ?? prev.relationship_type,
            referral_source: parsedData.referral_source ?? prev.referral_source,
            net_worth_estimate: parsedData.net_worth_estimate ?? prev.net_worth_estimate,
            investment_interests: parsedData.investment_interests ?? prev.investment_interests,
            business_size: parsedData.business_size ?? prev.business_size,
            revenue_estimate: parsedData.revenue_estimate ?? prev.revenue_estimate,
            birthday: parsedData.birthday ?? prev.birthday,
            spouse_name: parsedData.spouse_name ?? prev.spouse_name,
            children_names: parsedData.children_names ?? prev.children_names,
            interests: parsedData.interests ?? prev.interests,
            education: parsedData.education ?? prev.education,
            notes: parsedData.notes ?? prev.notes,
            marketing_consent: typeof parsedData.marketing_consent === 'boolean' ? parsedData.marketing_consent : prev.marketing_consent,
            is_priority: typeof parsedData.is_priority === 'boolean' ? parsedData.is_priority : prev.is_priority,
        }));
    };

    // Build strict schema prompt for Gemini based on our contact JSON shape (mirrors vision parser)
    const buildSmartFillPrompt = (raw: string) => `You are a world-class data extractor. Analyze the pasted text and extract normalized contact fields.

STRICT OUTPUT RULES:
1) Return ONLY a JSON object, no markdown or commentary.
2) Use null for unknown fields.
3) Normalize phone numbers to E.164 if possible (e.g., +15551234567), else a clean international format.
4) Ensure URLs include https:// when appropriate.
5) If the name appears on one line, split into first_name and last_name when plausible.
6) Address fields: street (address), city, state (2-letter US code if known), country (full name), zip_code.
7) Do not hallucinate data; be conservative.

JSON SHAPE EXACTLY:
{
  "first_name": "string or null",
  "last_name": "string or null",
  "title": "string or null",
  "company": "string or null",
  "industry": "string or null",
  "email": "string or null",
  "phone": "string or null",
  "mobile": "string or null",
  "address": "string or null",
  "city": "string or null",
  "state": "string or null",
  "country": "string or null",
  "zip_code": "string or null",
  "linkedin_url": "string or null",
  "twitter_url": "string or null",
  "website_url": "string or null",
  "instagram_url": "string or null",
  "tier": "A" | "B" | "C" | "D" | "Unassigned",
  "status": "Not Contacted" | "Called" | "Follow-up" | "Meeting Scheduled" | "Closed",
  "relationship_type": "string or null",
  "referral_source": "string or null",
  "net_worth_estimate": "Under $1M" | "$1M - $5M" | "$5M - $10M" | "$10M - $50M" | "$50M+" | null,
  "investment_interests": "string or null",
  "business_size": "string or null",
  "revenue_estimate": "string or null",
  "birthday": "YYYY-MM-DD or null",
  "spouse_name": "string or null",
  "children_names": "string or null",
  "interests": "string or null",
  "education": "string or null",
  "notes": "string or null",
  "marketing_consent": boolean,
  "is_priority": boolean
}

TEXT TO PARSE:
${raw}`;

    const parseSmartFill = async (text: string) => {
        if (!text.trim()) return;
        setSmartFillLoading(true);
        setSmartFillError(null);
        try {
            const prompt = buildSmartFillPrompt(text);
            const response = await supabase.functions.invoke('gemini-ai', { body: { prompt } });
            if (response.error) {
                throw new Error(response.error.message || 'Failed to parse');
            }
            const raw = String(response.data?.response || '').trim();
            const cleaned = raw.replace(/```json\n?|```/g, '');
            const start = cleaned.indexOf('{');
            const end = cleaned.lastIndexOf('}');
            if (start === -1 || end === -1) throw new Error('AI did not return JSON');
            const json = cleaned.substring(start, end + 1);
            const parsed = JSON.parse(json);
            applyParsedFields(parsed);
        } catch (e: any) {
            console.error('Smart Fill parse failed', e);
            setSmartFillError(e?.message || 'Parse failed');
            alert(`Smart Fill failed: ${e?.message || e}`);
        } finally {
            setSmartFillLoading(false);
        }
    };

    const onSmartFillPaste: React.ClipboardEventHandler<HTMLTextAreaElement> = (e) => {
        try {
            const t = e.clipboardData?.getData('text') || '';
            if (t) {
                e.preventDefault();
                setSmartFillText(t);
                // Defer parse slightly so UI updates the textarea
                setTimeout(() => parseSmartFill(t), 0);
            }
        } catch { /* ignore */ }
    };
    const [snapPhone, setSnapPhone] = useState<string>('');
    const [snapPhotoFile, setSnapPhotoFile] = useState<File | null>(null);
    const [snapPhotoPreview, setSnapPhotoPreview] = useState<string>('');
    const [snapLocLabel, setSnapLocLabel] = useState<string>('');
    const [snapLocLoading, setSnapLocLoading] = useState<boolean>(false);
    const [snapLocError, setSnapLocError] = useState<string | null>(null);
    const { activeGroupId, vaultSlug } = useGroupContext();

    // Scan Business Card state
    const [scanLoading, setScanLoading] = useState(false);
    const [scanError, setScanError] = useState<string | null>(null);
    const [scanImageFile, setScanImageFile] = useState<File | null>(null);
    const [scanImagePreview, setScanImagePreview] = useState<string>('');
    const scanSectionRef = useRef<HTMLDivElement | null>(null);
    const [highlightScan, setHighlightScan] = useState(false);
    const [autoAddEnabled, setAutoAddEnabled] = useState<boolean>(true);
    const [scanBlurScore, setScanBlurScore] = useState<number | null>(null);
    const [scanQuality, setScanQuality] = useState<'sharp' | 'ok' | 'blurry' | null>(null);

    useEffect(() => {
        if (editingContact) {
            setFormData({
                ...editingContact,
                personal_details: editingContact.personal_details || ''
            });
        }
    }, [editingContact]);

    // Prefill notes from FourOptionQuickInput paste handoff
    useEffect(() => {
        try {
            const raw = sessionStorage.getItem('gv:incomingPaste');
            if (raw && !editingContact) {
                setFormData(prev => ({ ...prev, notes: prev.notes || raw }));
                sessionStorage.removeItem('gv:incomingPaste');
            }
        } catch { /* ignore */ }
    }, [editingContact]);

    const handleInputChange = (field: keyof Contact, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    // Listen for global scan trigger (morphed FAB) and trigger the hidden input/scan
    useEffect(() => {
        const handler = () => {
            try {
                const el = document.getElementById('full-card-input') as HTMLInputElement | null;
                if (el) el.click();
                else if (typeof (scanBusinessCard) === 'function') {
                    // Fallback to function if input not found
                    // @ts-ignore
                    scanBusinessCard();
                }
            } catch { /* no-op */ }
        };
        window.addEventListener('gv:triggerCardScan' as any, handler as any);
        return () => window.removeEventListener('gv:triggerCardScan' as any, handler as any);
    }, []);

    const parseQuickAddData = async () => {
        if (!quickAddText.trim()) return;

        setAiLoading(true);
        try {
            const prompt = `You are a contact data parser for a family office CRM system that syncs with GoHighLevel. Parse the following contact information and extract data into the exact JSON format specified below.

IMPORTANT INSTRUCTIONS:
1. Return ONLY valid JSON - no markdown, no explanations, no code blocks
2. Use null for any empty/missing values 
3. Ensure all field names match exactly as specified
4. Format data appropriately for both Supabase database and GoHighLevel CRM
5. Be conservative with assumptions - only extract data you're confident about

REQUIRED JSON STRUCTURE:
{
  "first_name": "string (required)",
  "last_name": "string (required)", 
  "title": "string or null",
  "company": "string or null",
  "industry": "string or null",
  "email": "string (valid email format) or null",
  "phone": "string (clean format: +1234567890) or null",
  "mobile": "string (clean format) or null",
  "address": "string (street address only) or null",
  "city": "string or null",
  "state": "string (2-letter code preferred) or null",
  "country": "string (full name) or null",
  "zip_code": "string or null",
  "linkedin_url": "string (full URL) or null",
  "twitter_url": "string (full URL) or null",
  "website_url": "string (full URL) or null",
  "instagram_url": "string (full URL) or null",
  "tier": "A" | "B" | "C" | "D" | "Unassigned",
  "status": "Not Contacted" | "Called" | "Follow-up" | "Meeting Scheduled" | "Closed",
  "relationship_type": "string or null",
  "referral_source": "string or null",
  "net_worth_estimate": "Under $1M" | "$1M - $5M" | "$5M - $10M" | "$10M - $50M" | "$50M+" | null,
  "investment_interests": "string or null",
  "business_size": "string or null",
  "revenue_estimate": "string or null",
  "birthday": "YYYY-MM-DD format or null",
  "spouse_name": "string or null",
  "children_names": "string or null",
  "interests": "string or null",
  "education": "string or null",
  "notes": "string or null",
  "marketing_consent": boolean,
  "is_priority": boolean
}

INPUT DATA:
${quickAddText}

Return the JSON object only:`;

            const { data, error } = await supabase.functions.invoke('gemini-ai', {
                body: { prompt }
            });

            if (error) throw new Error(error.message || 'Failed to parse contact data');

            if (data && data.response) {
                try {
                    let jsonStr = data.response.trim();
                    jsonStr = jsonStr.replace(/```json\n?/, '').replace(/\n?```/, '');
                    jsonStr = jsonStr.replace(/```\n?/, '').replace(/\n?```/, '');

                    const jsonStart = jsonStr.indexOf('{');
                    const jsonEnd = jsonStr.lastIndexOf('}') + 1;
                    if (jsonStart !== -1 && jsonEnd !== -1) {
                        jsonStr = jsonStr.substring(jsonStart, jsonEnd);
                    }

                    const parsedData = JSON.parse(jsonStr);

                    if (!parsedData.first_name || !parsedData.last_name) {
                        throw new Error('First name and last name are required');
                    }

                    const cleanedData = {
                        ...parsedData,
                        tier: ['A', 'B', 'C', 'D', 'Unassigned'].includes(parsedData.tier) ? parsedData.tier : 'Unassigned',
                        status: ['Not Contacted', 'Called', 'Follow-up', 'Meeting Scheduled', 'Closed'].includes(parsedData.status)
                            ? parsedData.status : 'Not Contacted',
                        marketing_consent: Boolean(parsedData.marketing_consent),
                        is_priority: Boolean(parsedData.is_priority),
                        phone: parsedData.phone ? parsedData.phone.replace(/[^\d+]/g, '') : null,
                        mobile: parsedData.mobile ? parsedData.mobile.replace(/[^\d+]/g, '') : null,
                        email: parsedData.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(parsedData.email) ? parsedData.email : null,
                        linkedin_url: parsedData.linkedin_url && !parsedData.linkedin_url.startsWith('http')
                            ? `https://${parsedData.linkedin_url}` : parsedData.linkedin_url,
                        twitter_url: parsedData.twitter_url && !parsedData.twitter_url.startsWith('http')
                            ? `https://${parsedData.twitter_url}` : parsedData.twitter_url,
                        website_url: parsedData.website_url && !parsedData.website_url.startsWith('http')
                            ? `https://${parsedData.website_url}` : parsedData.website_url,
                        instagram_url: parsedData.instagram_url && !parsedData.instagram_url.startsWith('http')
                            ? `https://${parsedData.instagram_url}` : parsedData.instagram_url,
                    };

                    setFormData(prev => ({ ...prev, ...cleanedData }));
                    setQuickAddText('');
                } catch (parseError) {
                    console.error('Failed to parse AI response as JSON:', parseError);
                    alert('AI response could not be parsed. Please check the format and try again, or enter data manually.');
                }
            }
        } catch (error) {
            console.error('Quick Add AI Error:', error);
            alert('Error parsing contact data. Please try again.');
        } finally {
            setAiLoading(false);
        }
    };

    const handleProfileImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setProfileImageFile(file);
            const reader = new FileReader();
            reader.onload = (e) => {
                setProfileImagePreview(e.target?.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSnapPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;
        setSnapPhotoFile(file);
        if (file) {
            const reader = new FileReader();
            reader.onload = (ev) => setSnapPhotoPreview(ev.target?.result as string);
            reader.readAsDataURL(file);
        } else {
            setSnapPhotoPreview('');
        }
    };

    const handleScanImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;
        setScanImageFile(file);
        if (file) {
            const reader = new FileReader();
            reader.onload = async (ev) => {
                const dataUrl = ev.target?.result as string;
                setScanImagePreview(dataUrl);
                // Estimate blur right away
                try {
                    const score = await estimateBlurScore(dataUrl);
                    setScanBlurScore(score);
                    const quality = score < 25 ? 'blurry' : score < 80 ? 'ok' : 'sharp';
                    setScanQuality(quality);
                } catch {
                    setScanBlurScore(null);
                    setScanQuality(null);
                }
                // Auto-run the scan once the preview is ready
                try { await scanBusinessCard(dataUrl); } catch { /* handled inside */ }
            };
            reader.readAsDataURL(file);
        } else {
            setScanImagePreview('');
            setScanBlurScore(null);
            setScanQuality(null);
        }
    };

    // Quick add from parsed fields (Smart Quick Add primary action)
    const quickAddFromParsed = async () => {
        // Use minimal validation similar to quickSnapCreate
        const haveName = Boolean((formData.first_name || '').trim() || (formData.last_name || '').trim());
        const phone = (formData.phone || formData.mobile || '').toString().trim();
        const email = (formData.email || '').toString().trim();
        if (!haveName && !phone && !email) {
            alert('Provide at least a name, phone, or email');
            return;
        }

        // Validate user context - this is critical for contact ownership
        if (!user?.id) {
            alert('User authentication error. Please refresh the page and try again.');
            console.error('CreateContactPage quickAddFromParsed: Missing user.id for contact creation');
            return;
        }

        setLoading(true);
        try {
            let profileUrl = formData.profile_image_url;
            if (profileImageFile) {
                profileUrl = await uploadImage(profileImageFile);
            }
            const first = (formData.first_name || '').toString().trim() || (email ? email.split('@')[0] : (phone ? 'Unknown' : 'New'));
            const last = (formData.last_name || '').toString().trim() || (phone || email ? 'Contact' : 'Contact');
            const payload: any = {
                ...formData,
                first_name: first,
                last_name: last,
                phone: phone || formData.phone || null,
                profile_image_url: profileUrl || null,
                updated_at: new Date().toISOString(),
                updated_by: user.id,
                user_id: user.id,
                created_at: new Date().toISOString(),
                created_by: user.id,
            };
            const saved = await createContactCore(supabase as any, payload);
            try { window.dispatchEvent(new CustomEvent('gv:contactCreated', { detail: { id: (saved as any).id } })); } catch { }
            const newContact = { id: (saved as any).id } as any;

            // Link to active group if available
            try {
                let targetGroupId: string | null = activeGroupId || null;
                if (!targetGroupId && vaultSlug) {
                    const bySlug = await findGroupBySlug(vaultSlug);
                    if (bySlug?.id) targetGroupId = bySlug.id;
                }
                if (targetGroupId) {
                    try { await addContactToGroup(newContact.id, targetGroupId); } catch { }
                }
            } catch { }

            // Geocode and persist coordinates immediately when a location is present
            try { await geocodeAndSave(newContact.id, payload as Partial<Contact>); } catch { }

            // Optional: sync to GHL
            setGhlSyncing(true);
            try { await GHLSync.syncContactToGHL(newContact.id, false); } catch { }
            finally { setGhlSyncing(false); }

            onSave();
            onBack();
        } catch (e) {
            console.error('Quick Add from parsed failed:', e);
            alert('Quick add failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Basic blur score using Laplacian variance over a downsized grayscale image
    const estimateBlurScore = (dataUrl: string): Promise<number> => {
        return new Promise((resolve, reject) => {
            try {
                const img = new Image();
                img.onload = () => {
                    try {
                        const maxSide = 256;
                        const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
                        const w = Math.max(1, Math.round(img.width * scale));
                        const h = Math.max(1, Math.round(img.height * scale));
                        const canvas = document.createElement('canvas');
                        canvas.width = w; canvas.height = h;
                        const ctx = canvas.getContext('2d');
                        if (!ctx) return resolve(0);
                        ctx.drawImage(img, 0, 0, w, h);
                        const { data } = ctx.getImageData(0, 0, w, h);
                        // grayscale
                        const gray = new Float32Array(w * h);
                        for (let i = 0, j = 0; i < data.length; i += 4, j++) {
                            const r = data[i], g = data[i + 1], b = data[i + 2];
                            gray[j] = 0.299 * r + 0.587 * g + 0.114 * b;
                        }
                        // Laplacian kernel: [0,1,0;1,-4,1;0,1,0]
                        let sumSq = 0;
                        let count = 0;
                        const idx = (x: number, y: number) => y * w + x;
                        for (let y = 1; y < h - 1; y++) {
                            for (let x = 1; x < w - 1; x++) {
                                const c = gray[idx(x, y)] * -4
                                    + gray[idx(x - 1, y)]
                                    + gray[idx(x + 1, y)]
                                    + gray[idx(x, y - 1)]
                                    + gray[idx(x, y + 1)];
                                sumSq += c * c;
                                count++;
                            }
                        }
                        const variance = count > 0 ? sumSq / count : 0;
                        resolve(variance);
                    } catch (e) {
                        resolve(0);
                    }
                };
                img.onerror = () => resolve(0);
                img.src = dataUrl;
            } catch (e) {
                reject(e);
            }
        });
    };

    // Build a best-effort location string from available fields, mirroring the map logic
    const buildLocationString = (c: Partial<Contact>): string | null => {
        const locationSources = [
            c.address_1_formatted,
            c.address_1_city && c.address_1_region && `${c.address_1_city}, ${c.address_1_region}`,
            c.address_1_city && c.address_1_country && `${c.address_1_city}, ${c.address_1_country}`,
            c.address_2_formatted,
            c.address_2_city && c.address_2_region && `${c.address_2_city}, ${c.address_2_region}`,
            c.address_2_city && c.address_2_country && `${c.address_2_city}, ${c.address_2_country}`,
            c.city && c.state && `${c.city}, ${c.state}`,
            c.city && c.country && `${c.city}, ${c.country}`,
            c.address,
            c.location_info,
            c.business_city && c.business_state && `${c.business_city}, ${c.business_state}`,
            c.business_city && c.business_country && `${c.business_city}, ${c.business_country}`,
        ].filter(Boolean) as string[];
        for (const loc of locationSources) {
            const s = (loc || '').trim();
            if (s && s.length > 2 && !/^[A-Z]{1,3}$/.test(s) && (s.includes(' ') || s.includes(','))) return s;
        }
        return null;
    };

    // Load Google Maps core using the same Supabase function as the map to retrieve API key
    const loadGoogleMapsCore = async (): Promise<any | null> => {
        try {
            if ((window as any).google?.maps) return (window as any).google as any;
            const { data, error } = await supabase.functions.invoke('get-google-maps-key');
            if (error) throw error;
            if (!data?.apiKey) throw new Error('No Google Maps API key returned');
            await new Promise<void>((resolve, reject) => {
                const existing = document.getElementById('google-maps-script') as HTMLScriptElement | null;
                if (existing) {
                    // If script exists but maps is not yet ready, wait a tick
                    const int = window.setInterval(() => {
                        if ((window as any).google?.maps) {
                            window.clearInterval(int);
                            resolve();
                        }
                    }, 100);
                    // Also set a timeout guard
                    window.setTimeout(() => {
                        window.clearInterval(int);
                        ((window as any).google?.maps) ? resolve() : reject(new Error('Google Maps failed to initialize in time'));
                    }, 8000);
                    return;
                }
                (window as any).initMapCoreForCreate = () => resolve();
                const script = document.createElement('script');
                script.id = 'google-maps-script';
                script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(data.apiKey)}&callback=initMapCoreForCreate`;
                script.async = true;
                script.defer = true;
                script.onerror = () => reject(new Error('Failed to load Google Maps script'));
                document.body.appendChild(script);
            });
            return (window as any).google as any;
        } catch (e) {
            console.error('Google Maps load error (create page):', e);
            return null;
        }
    };

    // Geocode a location string and persist lat/lng to the contact
    const geocodeAndSave = async (contactId: string, contactData: Partial<Contact>) => {
        const loc = buildLocationString(contactData);
        if (!loc) return; // nothing to do
        const g: any = await loadGoogleMapsCore();
        if (!g) {
            console.error('Geocoding skipped: Google Maps not available');
            return;
        }
        try {
            const geocoder = new g.maps.Geocoder();
            const result: any = await new Promise((resolve, reject) => {
                geocoder.geocode({ address: loc }, (results: any, status: any) => {
                    if (status === 'OK' && results && results[0]) resolve(results[0]); else reject(new Error('Geocoding failed: ' + status));
                });
            });
            const p = result.geometry.location;
            const latitude = typeof p.lat === 'function' ? p.lat() : p.lat;
            const longitude = typeof p.lng === 'function' ? p.lng() : p.lng;
            await updateContactCore(supabase as any, contactId, { latitude, longitude } as any);
        } catch (e) {
            console.error('Failed to geocode and save contact coordinates:', e);
        }
    };

    const dataUrlToBase64 = (dataUrl: string): { base64: string; mime: string } => {
        const [header, data] = dataUrl.split(',');
        const mime = header.match(/data:(.*);base64/)?.[1] || 'image/jpeg';
        return { base64: data, mime };
    };

    const uploadImage = async (file: File): Promise<string> => {
        // Use shared, compressed upload via core-backed helper; store under images/contacts
        return uploadCompressedImage(file, 'contacts');
    };

    const handleSave = async () => {
        if (!formData.first_name || !formData.last_name) {
            alert('First name and last name are required');
            return;
        }

        // Validate user context - this is critical for contact ownership
        if (!user?.id) {
            alert('User authentication error. Please refresh the page and try again.');
            console.error('CreateContactPage: Missing user.id for contact creation');
            return;
        }

        setLoading(true);
        try {
            let profileImageUrl = formData.profile_image_url;

            if (profileImageFile) {
                profileImageUrl = await uploadImage(profileImageFile);
            }

            const cleanedFormData = {
                ...formData,
                profile_image_url: profileImageUrl,
                next_follow_up: formData.next_follow_up || null,
                birthday: formData.birthday || null,
            };

            const dataToSave = {
                ...cleanedFormData,
                updated_at: new Date().toISOString(),
                updated_by: user.id,
            };

            const validStatuses = ['Not Contacted', 'Called', 'Follow-up', 'Meeting Scheduled', 'Closed'];
            const validTiers = ['A', 'B', 'C', 'D', 'Unassigned'];

            if (dataToSave.status && !validStatuses.includes(dataToSave.status)) {
                dataToSave.status = 'Not Contacted';
            }

            if (dataToSave.tier && !validTiers.includes(dataToSave.tier)) {
                dataToSave.tier = 'Unassigned';
            }

            let savedContactId: string;
            let isUpdate = false;

            if (editingContact?.id) {
                // Ensure NOT NULL constraints are respected
                const safeUpdate = {
                    ...dataToSave,
                    first_name: (dataToSave as any).first_name ? String((dataToSave as any).first_name).trim() : ((formData.first_name || '') as string).trim(),
                    last_name: (dataToSave as any).last_name ? String((dataToSave as any).last_name).trim() : ((formData.last_name || '') as string).trim(),
                };
                await updateContactCore(supabase as any, editingContact.id, safeUpdate as any);
                // Attempt geocoding on update if location present
                try { await geocodeAndSave(editingContact.id, safeUpdate as Partial<Contact>); } catch (e) { console.warn('Geocode on update failed', e); }
                savedContactId = editingContact.id;
                isUpdate = true;
            } else {
                // Ensure NOT NULL constraints are respected
                const safeInsert = {
                    ...dataToSave,
                    first_name: (dataToSave as any).first_name ? String((dataToSave as any).first_name).trim() : '',
                    last_name: (dataToSave as any).last_name ? String((dataToSave as any).last_name).trim() : '',
                    user_id: user.id,
                    created_by: user.id,
                    updated_by: user.id,
                } as any;
                const newContact = await createContactCore(supabase as any, safeInsert);
                savedContactId = (newContact as any).id;

                // Link to active group if selected
                // Resolve target group to link the new contact, even if context isn't ready yet
                try {
                    let targetGroupId: string | null = activeGroupId || null;
                    if (!targetGroupId && vaultSlug) {
                        const bySlug = await findGroupBySlug(vaultSlug);
                        if (bySlug?.id) targetGroupId = bySlug.id;
                    }
                    if (targetGroupId) {
                        try { await addContactToGroup(savedContactId, targetGroupId); } catch (e) { console.warn('Failed to link contact to group', e); }
                    }
                } catch (linkErr) {
                    console.warn('Group resolution/linking failed', linkErr);
                }

                // Geocode and persist coordinates immediately when a location is present
                try { await geocodeAndSave(savedContactId, safeInsert as Partial<Contact>); } catch (e) { console.warn('Geocode on create failed', e); }
            }

            setGhlSyncing(true);
            try {
                const syncResult = await GHLSync.syncContactToGHL(savedContactId, isUpdate);
                if (!syncResult.success) {
                    console.warn('GHL Sync failed:', syncResult.error);
                }
            } catch (ghlError) {
                console.warn('GHL Sync error:', ghlError);
            } finally {
                setGhlSyncing(false);
            }

            onSave();
            onBack();
        } catch (error) {
            console.error('Error saving contact:', error);
            alert('Error saving contact. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const cleanParsedData = (parsedData: any) => {
        const cleaned = {
            ...parsedData,
            tier: ['A', 'B', 'C', 'D', 'Unassigned'].includes(parsedData.tier) ? parsedData.tier : 'Unassigned',
            status: ['Not Contacted', 'Called', 'Follow-up', 'Meeting Scheduled', 'Closed'].includes(parsedData.status)
                ? parsedData.status : 'Not Contacted',
            marketing_consent: Boolean(parsedData.marketing_consent),
            is_priority: Boolean(parsedData.is_priority),
            phone: parsedData.phone ? String(parsedData.phone).replace(/[^\d+]/g, '') : null,
            mobile: parsedData.mobile ? String(parsedData.mobile).replace(/[^\d+]/g, '') : null,
            email: parsedData.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(parsedData.email) ? parsedData.email : null,
            linkedin_url: parsedData.linkedin_url && !String(parsedData.linkedin_url).startsWith('http')
                ? `https://${parsedData.linkedin_url}` : parsedData.linkedin_url,
            twitter_url: parsedData.twitter_url && !String(parsedData.twitter_url).startsWith('http')
                ? `https://${parsedData.twitter_url}` : parsedData.twitter_url,
            website_url: parsedData.website_url && !String(parsedData.website_url).startsWith('http')
                ? `https://${parsedData.website_url}` : parsedData.website_url,
            instagram_url: parsedData.instagram_url && !String(parsedData.instagram_url).startsWith('http')
                ? `https://${parsedData.instagram_url}` : parsedData.instagram_url,
        };
        return cleaned;
    };

    const scrollToScanSection = () => {
        try {
            scanSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setHighlightScan(true);
            window.setTimeout(() => setHighlightScan(false), 1800);
        } catch { /* no-op */ }
    };

    const scanBusinessCard = async (overrideDataUrl?: string) => {
        setScanError(null);
        if (!overrideDataUrl && !scanImageFile && !scanImagePreview) {
            // If we don't yet have a photo, open the file picker
            try { (document.getElementById('full-card-input') as HTMLInputElement | null)?.click(); } catch { }
            return;
        }
        setScanLoading(true);
        try {
            let dataUrl = overrideDataUrl || scanImagePreview;
            let mime = 'image/jpeg';
            if (!dataUrl && scanImageFile) {
                const reader = new FileReader();
                dataUrl = await new Promise<string>((resolve) => {
                    reader.onload = (ev) => resolve(ev.target?.result as string);
                    reader.readAsDataURL(scanImageFile);
                });
            }
            const { base64, mime: detectedMime } = dataUrlToBase64(dataUrl);
            mime = detectedMime || mime;

            // If auto‑add is enabled, ensure we have a quality estimate
            if (autoAddEnabled && scanQuality == null) {
                try {
                    const score = await estimateBlurScore(dataUrl);
                    setScanBlurScore(score);
                    const quality = score < 25 ? 'blurry' : score < 80 ? 'ok' : 'sharp';
                    setScanQuality(quality);
                } catch { /* ignore */ }
            }

            const visionPrompt = undefined; // default in function enforces JSON schema
            const { data, error } = await supabase.functions.invoke('gemini-vision-parse-card', {
                body: { imageBase64: base64, mimeType: mime, prompt: visionPrompt }
            });
            if (error) throw new Error(error.message || 'Failed to parse card');

            if (data && data.response) {
                let jsonStr = String(data.response).trim();
                jsonStr = jsonStr.replace(/```json\n?/, '').replace(/\n?```/, '');
                jsonStr = jsonStr.replace(/```\n?/, '').replace(/\n?```/, '');
                const jsonStart = jsonStr.indexOf('{');
                const jsonEnd = jsonStr.lastIndexOf('}') + 1;
                if (jsonStart !== -1 && jsonEnd !== -1) {
                    jsonStr = jsonStr.substring(jsonStart, jsonEnd);
                }
                try {
                    const parsed = JSON.parse(jsonStr);
                    const cleaned = cleanParsedData(parsed);
                    setFormData(prev => ({ ...prev, ...cleaned }));

                    // Auto‑add logic when confident
                    const hasKeyFields = !!(cleaned.first_name) && (
                        !!cleaned.last_name || !!cleaned.email || !!cleaned.phone || !!cleaned.mobile
                    );
                    const confident = (scanQuality === 'sharp' || scanQuality === 'ok');
                    if (autoAddEnabled && hasKeyFields && confident) {
                        await autoCreateFromCard(cleaned);
                        return; // stop regular flow after navigation
                    }
                } catch (e) {
                    console.error('Failed to parse card JSON', e, jsonStr);
                    setScanError('AI response could not be parsed. Please try again.');
                }
            }
        } catch (e: any) {
            console.error('Scan card error', e);
            setScanError(e?.message || 'Failed to scan card');
        } finally {
            setScanLoading(false);
        }
    };

    const autoCreateFromCard = async (cleaned: any) => {
        // Validate user context - this is critical for contact ownership
        if (!user?.id) {
            console.error('CreateContactPage autoCreateFromCard: Missing user.id for contact creation');
            setScanError('User authentication error. Please refresh the page and try again.');
            return;
        }

        try {
            // Upload card image first to attach to contact
            let cardUrl: string | null = null;
            if (scanImageFile) {
                cardUrl = await uploadImage(scanImageFile);
            }
            const first = (cleaned.first_name || 'Card').toString().trim() || 'Card';
            const last = (cleaned.last_name || 'Contact').toString().trim() || 'Contact';
            const payload: any = {
                ...cleaned,
                first_name: first,
                last_name: last,
                updated_at: new Date().toISOString(),
                updated_by: user.id,
                user_id: user.id,
                created_at: new Date().toISOString(),
                created_by: user.id,
            };
            if (cardUrl) {
                try {
                    const prev = Array.isArray(cleaned.additional_images) ? cleaned.additional_images : [];
                    payload.additional_images = [...prev, cardUrl];
                } catch { payload.additional_images = [cardUrl]; }
            }

            const newC = await createContactCore(supabase as any, payload);
            try { window.dispatchEvent(new CustomEvent('gv:contactCreated', { detail: { id: (newC as any).id } })); } catch { }
            const newContact = { id: (newC as any).id };

            // Link to active group or vault slug if possible
            try {
                let targetGroupId: string | null = activeGroupId || null;
                if (!targetGroupId && vaultSlug) {
                    const bySlug = await findGroupBySlug(vaultSlug);
                    if (bySlug?.id) targetGroupId = bySlug.id;
                }
                if (targetGroupId) {
                    try { await addContactToGroup(newContact.id, targetGroupId); } catch { }
                }
            } catch { }

            // Geocode and persist coordinates immediately when a location is present
            try { await geocodeAndSave(newContact.id, payload as Partial<Contact>); } catch (e) { console.warn('Geocode on auto-create failed', e); }

            // Optional sync
            try { await GHLSync.syncContactToGHL(newContact.id, false); } catch { }

            // Navigate to the newly created contact
            try {
                window.dispatchEvent(new CustomEvent('gv:openContact' as any, { detail: { contactId: newContact.id } } as any));
            } catch { /* ignore */ }
            onSave();
            onBack();
        } catch (e) {
            console.error('Auto-create from card failed', e);
            setScanError('Auto-add failed. Review the fields and add manually.');
        }
    };

    const quickUseMyLocation = async () => {
        setSnapLocError(null);
        if (!navigator.geolocation) {
            setSnapLocError('Location not supported');
            return;
        }
        setSnapLocLoading(true);
        try {
            const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 });
            });
            await loadGoogleMapsPlaces();
            // @ts-ignore
            const geocoder = new window.google.maps.Geocoder();
            await new Promise<void>((resolve) => {
                geocoder.geocode({ location: { lat: pos.coords.latitude, lng: pos.coords.longitude } }, (results: any[], status: any) => {
                    if (status !== (window as any).google.maps.GeocoderStatus.OK || !results || results.length === 0) {
                        setSnapLocError('Could not resolve address');
                        setSnapLocLoading(false);
                        resolve();
                        return;
                    }
                    const best = results[0];
                    const parts = extractAddressParts(best.address_components);
                    const label = shortAddressLabel(null, parts.city || null, parts.state || null, parts.country || null) || best.formatted_address;
                    setSnapLocLabel(label);
                    // Pre-fill formData location fields
                    setFormData(prev => ({
                        ...prev,
                        city: parts.city || prev.city,
                        state: parts.state || prev.state,
                        country: parts.country || prev.country,
                        address: best.formatted_address || prev.address,
                    }));
                    setSnapLocLoading(false);
                    resolve();
                });
            });
        } catch (e) {
            setSnapLocError('Unable to get current location');
            setSnapLocLoading(false);
        }
    };

    // Compose a friendly intro and copy
    const buildIntroMessage = (): string => {
        const name = `${(formData.first_name || '').trim()} ${(formData.last_name || '').trim()}`.trim() || 'there';
        const where = snapLocLabel ? ` near ${snapLocLabel}` : '';
        return `Hey ${name ? name.split(' ')[0] : 'there'}, this is ${user?.email || 'me'}. Just saved your contact${where}. I'll reach out soon.`;
    };

    const copyIntro = async () => {
        try {
            await navigator.clipboard.writeText(buildIntroMessage());
            // Optional: toast could be triggered here
        } catch { }
    };

    const shareIntro = async () => {
        const msg = buildIntroMessage();
        // Use Web Share API if available
        // @ts-ignore
        if (navigator.share) {
            try {
                // @ts-ignore
                await navigator.share({ text: msg });
                return;
            } catch { }
        }
        // Fallback to copy
        try { await navigator.clipboard.writeText(msg); } catch { }
    };

    const quickSnapCreate = async () => {
        // Minimal validation: require either a name or a phone
        const haveName = Boolean((formData.first_name || '').trim() || (formData.last_name || '').trim());
        const phone = (snapPhone || formData.phone || '').trim();
        if (!haveName && !phone) {
            alert('Enter at least a name or a phone number');
            return;
        }

        // Validate user context - this is critical for contact ownership
        if (!user?.id) {
            alert('User authentication error. Please refresh the page and try again.');
            console.error('CreateContactPage quickSnapCreate: Missing user.id for contact creation');
            return;
        }

        setLoading(true);
        try {
            let profileUrl = formData.profile_image_url;
            // Prefer uploaded profile image at top if present
            if (profileImageFile) {
                profileUrl = await uploadImage(profileImageFile);
            }
            const first = (formData.first_name || '').trim() || (phone ? 'Unknown' : 'New');
            const last = (formData.last_name || '').trim() || (phone ? 'Contact' : 'Contact');
            const payload: any = {
                ...formData,
                first_name: first,
                last_name: last,
                phone: phone || null,
                profile_image_url: profileUrl || null,
                updated_at: new Date().toISOString(),
                updated_by: user.id,
                user_id: user.id,
                created_at: new Date().toISOString(),
                created_by: user.id,
            };
            const newC = await createContactCore(supabase as any, payload);
            try { window.dispatchEvent(new CustomEvent('gv:contactCreated', { detail: { id: (newC as any).id } })); } catch { }
            const newContact = { id: (newC as any).id };

            // Link to active group if selected
            if (activeGroupId) {
                try { await addContactToGroup(newContact.id, activeGroupId); } catch (e) { console.warn('Failed to link contact to group', e); }
            }

            // Geocode and persist coordinates immediately when a location is present
            try { await geocodeAndSave(newContact.id, payload as Partial<Contact>); } catch (e) { console.warn('Geocode on quick create failed', e); }

            // Optional: sync to GHL
            setGhlSyncing(true);
            try {
                const syncResult = await GHLSync.syncContactToGHL(newContact.id, false);
                if (!syncResult.success) console.warn('GHL Sync failed (quick):', syncResult.error);
            } catch (e) {
                console.warn('GHL Sync error (quick):', e);
            } finally {
                setGhlSyncing(false);
            }

            onSave();
            onBack();
        } catch (e) {
            console.error('Quick Snap Create failed:', e);
            alert('Quick create failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-black">
            {/* Header */}
            <div className="bg-black border-b border-gray-800 px-4 sm:px-6 py-3 sm:py-4">
                <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={handleBack}
                            className="p-2 hover:bg-gray-800 rounded-lg transition-colors text-gray-400 hover:text-white"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <div className="text-left">
                            <h1 className="text-xl sm:text-2xl font-light text-white tracking-wide leading-snug">
                                {editingContact ? 'Edit Contact' : 'Add New Contact'}
                            </h1>
                            <p className="text-xs sm:text-sm text-gray-400 mt-1">Add contact information to your secure vault</p>
                        </div>
                    </div>
                    <div className="flex items-center justify-center sm:justify-end relative">
                        {/* Single toggle button: go to Quick Add (wide, dark, centered on mobile) */}
                        {!editingContact && (
                            <button
                                type="button"
                                className="w-full sm:w-auto sm:min-w-[160px] inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm rounded-lg border border-gray-700 bg-gray-900 text-gray-200 hover:bg-gray-800"
                                onClick={() => { onToggleQuickAdd?.(); }}
                                title="Switch to Quick Add"
                            >
                                Quick Add
                            </button>
                        )}
                        {/* Hidden input for business card photo (kept for potential future triggers) */}
                        <input id="full-card-input" type="file" accept="image/*" capture="environment" className="hidden" onChange={handleScanImageChange} />
                    </div>
                </div>
            </div>

            {/* Content */}
            {/* Match header/Quick Add horizontal padding; add bottom padding for footer bar */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-8 pb-56">
                {/* Smart Fill block (below photo/name) */}
                {!editingContact && (
                    <div className="bg-gray-900 border border-gray-700 rounded-lg p-4">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <div className="text-white font-medium">Smart Fill</div>
                                <div className="text-xs text-gray-400">Paste details here — auto-parsed on paste.</div>
                            </div>
                            {smartFillLoading && (
                                <div className="inline-flex items-center gap-2 text-gray-300 text-sm">
                                    <Loader2 className="w-4 h-4 animate-spin" /> Parsing…
                                </div>
                            )}
                        </div>
                        <div className="mt-3">
                            <textarea
                                value={smartFillText}
                                onChange={(e) => setSmartFillText(e.target.value)}
                                onPaste={onSmartFillPaste}
                                rows={3}
                                placeholder="Paste details here…"
                                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <div className="mt-3 flex justify-center">
                            <button
                                type="button"
                                onClick={() => parseSmartFill(smartFillText)}
                                disabled={smartFillLoading || !smartFillText.trim()}
                                className="w-full sm:w-1/2 px-5 py-2.5 rounded-lg border border-gray-700 bg-gray-900 text-gray-200 hover:bg-gray-800 disabled:opacity-50"
                                title="Parse and fill"
                            >Fill</button>
                        </div>
                        {smartFillText && (
                            <div className="mt-1 text-center">
                                <button
                                    type="button"
                                    onClick={() => { setSmartFillText(''); setSmartFillError(null); }}
                                    className="bg-transparent p-0 text-sm text-gray-400 hover:text-gray-200"
                                >Clear</button>
                            </div>
                        )}
                        {smartFillError && (
                            <div className="text-sm text-red-400 mt-2 text-center">{smartFillError}</div>
                        )}
                    </div>
                )}
                {/* Top-centered Profile Header */}
                <div className="bg-gray-900 border border-gray-700 rounded-lg p-6">
                    <div className="flex flex-col items-center">
                        <div
                            className="relative w-28 h-28 md:w-32 md:h-32 bg-gray-800 border border-gray-700 rounded-lg flex items-center justify-center overflow-hidden cursor-pointer hover:opacity-90"
                            onClick={() => document.getElementById('profile-image-input')?.click()}
                            title="Upload profile photo"
                        >
                            {profileImagePreview ? (
                                <img src={profileImagePreview} alt="Profile preview" className="w-full h-full object-cover" />
                            ) : (
                                <div className="flex flex-col items-center text-gray-400">
                                    <Camera className="w-6 h-6 mb-1" />
                                    <span className="text-xs">Add Photo</span>
                                </div>
                            )}
                        </div>
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 w-full max-w-xl">
                            <input
                                type="text"
                                value={formData.first_name}
                                onChange={(e) => handleInputChange('first_name', e.target.value)}
                                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="First name"
                            />
                            <input
                                type="text"
                                value={formData.last_name}
                                onChange={(e) => handleInputChange('last_name', e.target.value)}
                                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Last name"
                            />
                        </div>
                    </div>
                </div>

                {/* Quick mode removed from this page; use the QuickAddContact route instead */}

                {/* Full form sections (no business card scan here) */}
                {true && (
                    <>
                        {/* Basic Information */}
                        <div className="bg-gray-900 border border-gray-700 rounded-lg p-6">
                            <h3 className="text-lg font-medium text-white mb-6">Basic Information</h3>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        First Name <span className="text-red-400">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.first_name}
                                        onChange={(e) => handleInputChange('first_name', e.target.value)}
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Last Name <span className="text-red-400">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.last_name}
                                        onChange={(e) => handleInputChange('last_name', e.target.value)}
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => handleInputChange('email', e.target.value)}
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Phone</label>
                                    <input
                                        type="tel"
                                        value={formData.phone}
                                        onChange={(e) => handleInputChange('phone', e.target.value)}
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Company</label>
                                    <input
                                        type="text"
                                        value={formData.company}
                                        onChange={(e) => handleInputChange('company', e.target.value)}
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Title</label>
                                    <input
                                        type="text"
                                        value={formData.title}
                                        onChange={(e) => handleInputChange('title', e.target.value)}
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Tier</label>
                                    <CustomSelect
                                        options={[
                                            { value: 'Unassigned', label: 'Unassigned' },
                                            { value: 'A', label: 'Tier A - High Priority' },
                                            { value: 'B', label: 'Tier B - Medium Priority' },
                                            { value: 'C', label: 'Tier C - Low Priority' },
                                            { value: 'D', label: 'Tier D - Archive' }
                                        ]}
                                        value={formData.tier}
                                        onChange={(value) => handleInputChange('tier', value)}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                                    <CustomSelect
                                        options={[
                                            { value: 'Not Contacted', label: 'Not Contacted' },
                                            { value: 'Called', label: 'Called' },
                                            { value: 'Follow-up', label: 'Follow-up' },
                                            { value: 'Meeting Scheduled', label: 'Meeting Scheduled' },
                                            { value: 'Closed', label: 'Closed' }
                                        ]}
                                        value={formData.status}
                                        onChange={(value) => handleInputChange('status', value)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Additional Information */}
                        <div className="bg-gray-900 border border-gray-700 rounded-lg p-6">
                            <h3 className="text-lg font-medium text-white mb-6">Additional Information</h3>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Net Worth Estimate</label>
                                    <CustomSelect
                                        options={[
                                            { value: '', label: 'Select Range' },
                                            { value: 'Under $1M', label: 'Under $1M' },
                                            { value: '$1M - $5M', label: '$1M - $5M' },
                                            { value: '$5M - $10M', label: '$5M - $10M' },
                                            { value: '$10M - $50M', label: '$10M - $50M' },
                                            { value: '$50M+', label: '$50M+' }
                                        ]}
                                        value={formData.net_worth_estimate}
                                        onChange={(value) => handleInputChange('net_worth_estimate', value)}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Investment Interests</label>
                                    <input
                                        type="text"
                                        value={formData.investment_interests}
                                        onChange={(e) => handleInputChange('investment_interests', e.target.value)}
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="e.g. Tech, Real Estate, Startups"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">LinkedIn URL</label>
                                    <input
                                        type="url"
                                        value={formData.linkedin_url}
                                        onChange={(e) => handleInputChange('linkedin_url', e.target.value)}
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="https://linkedin.com/in/username"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Website URL</label>
                                    <input
                                        type="url"
                                        value={formData.website_url}
                                        onChange={(e) => handleInputChange('website_url', e.target.value)}
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="https://website.com"
                                    />
                                </div>
                            </div>

                            <div className="mt-6">
                                <label className="block text-sm font-medium text-gray-300 mb-2">Notes</label>
                                <textarea
                                    value={formData.notes}
                                    onChange={(e) => handleInputChange('notes', e.target.value)}
                                    rows={4}
                                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Add any additional notes..."
                                />
                            </div>

                            <div className="mt-6">
                                <label className="block text-sm font-medium text-gray-300 mb-2">Marketing Consent</label>
                                <CustomSelect
                                    options={[
                                        { value: 'no', label: 'No - Do not contact for marketing' },
                                        { value: 'yes', label: 'Yes - Contact for marketing is okay' }
                                    ]}
                                    value={formData.marketing_consent ? 'yes' : 'no'}
                                    onChange={(value) => handleInputChange('marketing_consent', value === 'yes')}
                                />
                            </div>
                        </div>

                        {/* Action Buttons (hidden in-flow; replaced by sticky footer actions) */}
                        <div className="hidden justify-end space-x-4 pt-6">
                            <button
                                onClick={onBack}
                                className="px-6 py-2 border border-gray-600 text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={loading || ghlSyncing || !formData.first_name || !formData.last_name}
                                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        <span>{editingContact ? 'Saving…' : 'Adding…'}</span>
                                    </>
                                ) : ghlSyncing ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        <span>Syncing...</span>
                                    </>
                                ) : (
                                    <>
                                        <Check className="w-4 h-4" />
                                        <span>{editingContact ? 'Save' : 'Add'}</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </>
                )}
            </div>

            {/* Footer action bar: solid dashboard theme */}
            <div className="fixed bottom-0 inset-x-0 z-50">
                <div className="border-t border-gray-700 bg-gray-900">
                    {/* 3-column grid keeps the center truly centered even when side content changes width */}
                    <div className="max-w-4xl mx-auto px-6 py-4 grid grid-cols-3 items-center">
                        {/* Left: Primary actions */}
                        <div className="flex items-center gap-3 col-start-1 justify-start">
                            <button
                                type="button"
                                onClick={onBack}
                                className="px-4 py-2 border border-gray-700 text-gray-300 hover:bg-gray-700 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSave}
                                disabled={loading || ghlSyncing || !formData.first_name || !formData.last_name}
                                className="px-4 sm:px-6 py-2 bg-green-600 hover:bg-green-700 text-white border border-green-500 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2 transition-colors"
                            >
                                {loading || ghlSyncing ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        <span>{loading ? (editingContact ? 'Saving…' : 'Adding…') : 'Syncing…'}</span>
                                    </>
                                ) : (
                                    <>
                                        <Check className="w-4 h-4" />
                                        <span className="hidden sm:inline">{editingContact ? 'Save Contact' : 'Add Contact'}</span>
                                        <span className="sm:hidden">{editingContact ? 'Save' : 'Add'}</span>
                                    </>
                                )}
                            </button>
                        </div>
                        {/* Center: Scan Business Card - stays centered */}
                        <div className="col-start-2 flex justify-center">
                            <button
                                type="button"
                                onClick={() => {
                                    try {
                                        const el = document.getElementById('full-card-input') as HTMLInputElement | null;
                                        if (el) el.click();
                                    } catch { /* no-op */ }
                                }}
                                className="px-3 sm:px-4 py-2 border border-gray-700 text-gray-300 hover:bg-gray-700 inline-flex items-center gap-1 sm:gap-2 transition-colors"
                                title="Scan business card"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                    <rect x="2" y="5" width="20" height="14" rx="2" ry="2"></rect>
                                    <line x1="2" y1="10" x2="22" y2="10"></line>
                                </svg>
                                <span className="hidden xs:inline">Scan </span>
                                <span>Card</span>
                            </button>
                        </div>

                        {/* Right: empty spacer to preserve centering */}
                        <div className="col-start-3" />
                    </div>
                </div>
            </div>
        </div>
    );
}
